import { auth, db } from '@/firebase/init';
import { sendEmailVerification, User } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { IndexDB } from '@/utils/indexDB';
import { AuthLoaderFunction } from '@/types/auth';
import { AuthFlowNavbar } from '@/components/layouts/Navbar/AuthFlowNavbar';
import { VerifyEmailCard } from './VerifyEmailCard';
import { LoaderFunctionArgs, redirect, useSearchParams } from 'react-router-dom';
import { refreshSessionAndReloadApp } from './helpers';
import { NAV_ROUTES } from '@/utils/constants';
import {
  getHandleInvitationUrlFromSearchParams,
  getInvitationQueryString,
} from '@/utils/invitationUrlParams';

export enum ResendingStatus {
  Idle = 0,
  Sending = 1,
  Success = 2,
  Error = 3,
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [resendingStatus, setResendingStatus] = useState(ResendingStatus.Idle);
  const [isRefreshingVerification, setIsRefreshingVerification] = useState(false);
  const currentUser = auth.currentUser;

  const postVerificationTarget = useMemo(
    () => getHandleInvitationUrlFromSearchParams(searchParams) ?? NAV_ROUTES.root,
    [searchParams],
  );

  useEffect(() => {
    async function ensureVerifiedUsersAreRedirected() {
      try {
        if (!currentUser?.emailVerified) return;

        await refreshSessionAndReloadApp(currentUser, {
          afterNavigateTo: postVerificationTarget,
        });
      } catch (error) {
        console.error('Error checking verification safeguard:', error);
      }
    }

    void ensureVerifiedUsersAreRedirected();
  }, [currentUser, postVerificationTarget]);

  const handleRefreshVerification = async () => {
    try {
      setIsRefreshingVerification(true);
      await refreshSessionAndReloadApp(currentUser, {
        afterNavigateTo: postVerificationTarget,
      });
    } catch (error) {
      console.error('Error refreshing verification status:', error);
    } finally {
      setIsRefreshingVerification(false);
    }
  };

  const handleResend = async () => {
    const url = new URL(window.location.href);
    url.pathname = '/verify-email/action';
    url.search = getInvitationQueryString(searchParams) ?? '';
    try {
      setResendingStatus(ResendingStatus.Sending);
      await sendEmailVerification(auth.currentUser as User, {
        url: url.toString(),
      });
      setResendingStatus(ResendingStatus.Success);
    } catch (error) {
      setResendingStatus(ResendingStatus.Error);
    }
  };

  return (
    <>
      <AuthFlowNavbar />
      <section
        className="flex flex-1 flex-col items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url("/background.svg")' }}
      >
        <div className="w-full max-w-2xl rounded-lg px-4 py-3 sm:px-8 sm:py-4">
          <VerifyEmailCard
            handleResend={handleResend}
            resendingStatus={resendingStatus}
            handleRefreshVerification={handleRefreshVerification}
            isRefreshingVerification={isRefreshingVerification}
          />
        </div>
      </section>
    </>
  );
}

export const verifyEmailLoader = (async (args: LoaderFunctionArgs, user) => {
  const requestUrl = new URL(args.request.url);
  const invitationPath = getHandleInvitationUrlFromSearchParams(requestUrl.searchParams);
  const invitationQuery = getInvitationQueryString(requestUrl.searchParams);

  const userSnapshot = await getDoc(doc(db, 'users', user.userId));
  const userData = userSnapshot.data() as { isVerificationEmailSent: boolean };
  const isEmailVerified =
    auth.currentUser?.emailVerified ?? (await IndexDB.isEmailVerified());
  if (isEmailVerified) {
    if (invitationPath) {
      return redirect(invitationPath);
    }
    return redirect('/');
  }
  const isVerificationEmailSent = !!userData?.isVerificationEmailSent;

  if (!isVerificationEmailSent) {
    const actionUrl = new URL('/verify-email/action', requestUrl.origin);
    if (invitationQuery) {
      actionUrl.search = invitationQuery;
    }
    await sendEmailVerification(auth.currentUser as User, {
      url: actionUrl.toString(),
    });
    await updateDoc(userSnapshot.ref, { isVerificationEmailSent: true });
  }

  return { isEmailVerified, isVerificationEmailSent };
}) satisfies AuthLoaderFunction;
